import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Table, Tag, Switch } from "antd";
import { Link } from "react-router-dom";
import { EditInfo, DeleteModal, PreviewOffer } from ".";
import { getOffersList, patchOfferStatus } from "../../appRedux/actions/offers";

function ListView() {
  const dispatch = useDispatch();
  const offerList = useSelector((state) => state.offers.offerList);
  const queryInfo = useSelector((state) => state.offers.queryInfo);
  const totalRecords = useSelector((state) => state.offers.totalRecords);
  const pages = useSelector((state) => state.offers.pages);
  const [previewOffer, setPreviewOffer] = useState(null);

  useEffect(() => {
    dispatch(getOffersList(queryInfo)).catch(() => {});
  }, []);

  const handleTableChange = (pagination) => {
    const page = pagination?.current || 1;
    const size = pagination?.pageSize || queryInfo.size;
    dispatch(getOffersList({ ...queryInfo, page, size })).catch(() => {});
  };

  const discountLabel = (record) => {
    if (record.discountType === "PERCENT") return `${record.discountValue}% off`;
    return `₹${record.discountValue} off`;
  };

  const columns = [
    {
      title: "Title & Details",
      dataIndex: "title",
      key: "title",
      align: "left",
      render: (title, record) => {
        let validityUI = null;
        if (!record.startAt && !record.endAt) {
          validityUI = <Tag color="default" style={{ borderRadius: '6px', padding: '1px 6px', fontSize: '10px' }}>Lifetime</Tag>;
        } else {
          const start = record.startAt ? new Date(record.startAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "—";
          const end = record.endAt ? new Date(record.endAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : "—";
          validityUI = (
            <div style={{ display: "inline-flex", gap: "10px", fontSize: "11px", color: "#64748b", marginTop: "4px" }}>
              <div><i className="la la-calendar-check text-success" style={{ marginRight: "4px" }} />{start}</div>
              <div><i className="la la-calendar-times text-danger" style={{ marginRight: "4px" }} />{end}</div>
            </div>
          );
        }

        return (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span 
              onClick={() => setPreviewOffer(record)}
              className="offer-title-link"
              style={{ fontWeight: "600", fontSize: "14px", color: "#2f7edc" }}
              title="Click to preview offer"
            >
              {title || "—"}
            </span>
            {record.description && (
              <span style={{ fontSize: "12px", color: "#64748b", marginTop: "4px" }}>
                {record.description.length > 50 ? `${record.description.substring(0, 50)}...` : record.description}
              </span>
            )}
            <div style={{ marginTop: "6px" }}>{validityUI}</div>
          </div>
        );
      },
    },
    {
      title: "Level",
      dataIndex: "level",
      key: "level",
      align: "center",
      render: (level) => {
        const color =
          level === "GLOBAL"
            ? "geekblue"
            : level === "MERCHANT"
            ? "orange"
            : "purple";
        return <Tag color={color} style={{ borderRadius: '6px', padding: '3px 10px', fontWeight: 500 }}>{level}</Tag>;
      },
    },
    {
      title: "Target Scope",
      key: "scope",
      align: "center",
      render: (_, record) => {
        if (record.level === "MERCHANT")
          return record.merchant ? (
            <Tag color="cyan" style={{ borderRadius: '6px', padding: '3px 10px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <i className="la la-store" style={{ fontSize: "14px" }} />
              <span>{record.merchant}</span>
            </Tag>
          ) : (
            <span style={{ color: "#aaa" }}>—</span>
          );
        if (record.level === "COURSE")
          return record.courseId?.title ? (
            <Tag color="magenta" style={{ borderRadius: '6px', padding: '3px 10px', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
              <i className="la la-graduation-cap" style={{ fontSize: "14px" }} />
              <span style={{ display: "inline-block", maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={record.courseId.title}>
                {record.courseId.title}
              </span>
            </Tag>
          ) : (
            <span style={{ color: "#aaa" }}>{record.courseId || "—"}</span>
          );
        return <span style={{ color: "#aaa" }}>—</span>;
      },
    },
    {
      title: "Discount",
      key: "discount",
      align: "center",
      render: (_, record) => {
        const isPercent = record.discountType === "PERCENT";
        return (
          <Tag color="green" style={{ fontSize: '13px', padding: '3px 10px', borderRadius: '6px', fontWeight: 600 }}>
            {isPercent ? `${record.discountValue}%` : `₹${record.discountValue}`} off
          </Tag>
        );
      },
    },

    {
      title: "Promotions Active",
      key: "promotions",
      align: "center",
      render: (_, record) => {
        const promo = record.promotion || {};
        const activePromos = [];
        if (promo.showOnCoursePage) activePromos.push(<Tag key="l1" color="cyan" style={{ borderRadius: '4px', margin: '2px' }}>Patch</Tag>);
        if (promo.showOnHeaderStrip) activePromos.push(<Tag key="l2" color="blue" style={{ borderRadius: '4px', margin: '2px' }}>Strip</Tag>);
        if (promo.showInCarousel) activePromos.push(<Tag key="l3" color="purple" style={{ borderRadius: '4px', margin: '2px' }}>Carousel</Tag>);

        return activePromos.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: '4px' }}>
            {activePromos}
          </div>
        ) : (
          <span style={{ color: "#aaa" }}>—</span>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status, record) => {
        return (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "6px" }}>
            <Tag color={status === "ACTIVE" ? "success" : status === "DRAFT" ? "default" : "error"} style={{ borderRadius: '6px', padding: '2px 8px', margin: 0 }}>
              {status}
            </Tag>
            <Switch
              size="small"
              checked={status === "ACTIVE"}
              onChange={(checked) => {
                const newStatus = checked ? "ACTIVE" : "INACTIVE";
                dispatch(patchOfferStatus(record._id, newStatus))
                  .then(() => {
                    dispatch(getOffersList(queryInfo)).catch(() => {});
                  })
                  .catch(() => {});
              }}
            />
          </div>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      fixed: "right",
      onCell: () => ({ style: { background: "#fff" } }),
      onHeaderCell: () => ({ style: { background: "#fafafa" } }),
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "10px" }}>
          <EditInfo info={record} key="edit_offer" />
          <DeleteModal info={record} />
        </div>
      ),
    },
  ];

  return (
    <div className="table-responsive">
      <Table
        className="platform-hub-table"
        rowKey="_id"
        columns={columns}
        dataSource={offerList}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: queryInfo?.page || 1,
          pageSize: queryInfo?.size || 10,
          total: totalRecords,
          showSizeChanger: true,
          pageSizeOptions: ["5", "10", "20", "50"],
          showTotal: (total) => `Total ${total} offers`,
        }}
        onChange={handleTableChange}
      />
      {previewOffer && (
        <PreviewOffer
          info={previewOffer}
          visible={!!previewOffer}
          onClose={() => setPreviewOffer(null)}
        />
      )}
    </div>
  );
}

export default ListView;
